import useScrollAnimation from '../../hooks/useScrollAnimation';

interface Reason {
  icon: string;
  title: string;
  points: string[];
}

interface WhyChooseUsProps {
  eyebrow?: string;
  headline: string;
  subheadline?: string;
  reasons: Reason[];
  backgroundImage?: string;
}

const WhyChooseUsSection = ({
  eyebrow = 'Why Choose Us',
  headline,
  subheadline,
  reasons,
  backgroundImage,
}: WhyChooseUsProps) => {
  const { ref, isVisible } = useScrollAnimation();

  return (
    <section className="relative bg-[#0d0b07] overflow-hidden">
      <div className="flex flex-col lg:flex-row min-h-[700px]">
        {/* Left image panel (40% width) */}
        {backgroundImage && (
          <div className="relative lg:w-[42%] h-[320px] lg:h-auto overflow-hidden shrink-0">
            <img
              src={backgroundImage}
              alt="Polished Concrete Plus Craftsmanship"
              className="w-full h-full object-cover object-top"
            />
            {/* Gradient overlay */}
            <div className="absolute inset-0 bg-gradient-to-r from-[#0d0b07]/20 via-transparent to-[#0d0b07]/70" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0d0b07]/80 via-transparent to-transparent" />

            {/* Floating quote */}
            <div className="absolute bottom-10 left-8 right-8">
              <div className="border-l-2 border-[#c8a951] pl-5">
                <p
                  className="text-white/85 text-sm leading-relaxed italic"
                  style={{ fontFamily: '"Playfair Display", serif' }}
                >
                  "We create colorful and durable interior flooring finishes and decorative concrete hardscapes — for residential and commercial projects."
                </p>
                <p className="text-[#c8a951] text-[9px] uppercase tracking-[0.25em] mt-2">— Polished Concrete Plus, Est. 2003</p>
              </div>
            </div>
          </div>
        )}

        {/* Right panel — reasons */}
        <div className="lg:w-[58%] px-8 lg:px-14 py-20 flex flex-col justify-center">
          {/* Section header */}
          <div ref={ref} className={`anim-section ${isVisible ? 'is-visible' : ''} mb-12`}>
            {eyebrow && (
              <p className="text-[#c8a951] text-xs uppercase tracking-[0.35em] font-semibold mb-4">{eyebrow}</p>
            )}
            <h2
              className="text-4xl lg:text-5xl font-bold text-white leading-tight mb-4"
              style={{ fontFamily: '"Playfair Display", serif' }}
            >
              {headline}
            </h2>
            {subheadline && (
              <p className="text-white/45 text-sm leading-relaxed max-w-lg">{subheadline}</p>
            )}
          </div>

          {/* Reason cards */}
          <div className="space-y-5">
            {reasons.map((reason, i) => (
              <ReasonRow key={i} reason={reason} index={i} />
            ))}
          </div>

          {/* CTA row */}
          <div className="mt-12 pt-8 border-t border-[#c8a951]/15 flex flex-wrap gap-4">
            <a
              href="#contact"
              className="bg-[#c8a951] text-[#0a0a0a] text-xs font-bold px-8 py-4 uppercase tracking-[0.18em] hover:bg-[#e0c070] transition-colors whitespace-nowrap cursor-pointer"
            >
              Get a Free Estimate
            </a>
            <a
              href="/services"
              className="border border-white/20 text-white text-xs font-medium px-8 py-4 uppercase tracking-[0.18em] hover:border-[#c8a951] hover:text-[#c8a951] transition-all duration-300 whitespace-nowrap cursor-pointer"
            >
              View All Services
            </a>
          </div>
        </div>
      </div>

      {/* Gold accent corners */}
      <div className="absolute top-0 right-0 w-20 h-20 border-t border-r border-[#c8a951]/20 pointer-events-none" />
      <div className="absolute bottom-0 left-0 w-20 h-20 border-b border-l border-[#c8a951]/20 pointer-events-none" />
    </section>
  );
};

const ReasonRow = ({ reason, index }: { reason: Reason; index: number }) => {
  const { ref, isVisible } = useScrollAnimation();

  return (
    <div
      ref={ref}
      className={`anim-section ${isVisible ? 'is-visible' : ''} group flex gap-5 border border-[#c8a951]/10 p-6 hover:border-[#c8a951]/35 transition-colors duration-300 bg-[#111009]/60`}
      style={{ transitionDelay: `${index * 0.1}s` }}
    >
      <div className="w-11 h-11 flex items-center justify-center border border-[#c8a951]/25 shrink-0 group-hover:border-[#c8a951]/60 transition-colors duration-300">
        <i className={`${reason.icon} text-lg text-[#c8a951]`} />
      </div>
      <div>
        <h3
          className="text-base font-bold text-white mb-3"
          style={{ fontFamily: '"Playfair Display", serif' }}
        >
          {reason.title}
        </h3>
        <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2">
          {reason.points.map((pt, j) => (
            <li key={j} className="flex items-start gap-2 text-sm text-white/50">
              <i className="ri-checkbox-circle-fill text-[#c8a951] shrink-0 mt-0.5 text-xs" />
              {pt}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};

export default WhyChooseUsSection;
